//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { IncomingInvoicePeriodSectionProps } from "models/propTypes/incomingInvoicePropTypes";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Button, Grid } from "@mui/material";
import { GroupSectionInline } from "components/common/wintForms";
import { DetailsTableViewSmall, PeriodisationAdvancedDialog, StaticInfoMessage } from "components/common";
import DialogEvents from "components/common/dialogs/dialogEvents";
import { useIncomingInvoiceService } from "serviceHooks";
//#endregion
import clsx from "clsx";
import { AccrualHelper, LocalizationHelperInstance, PeriodisationRowsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const toPut = (
  value: IncomingInvoiceFormTypes.PeriodisationRowsFormValue,
  invoiceMonth: Date | undefined
): WintSuperkollAPIModel.IncomingInvoicePeriodisationPut => {
  // No rows also covers a split lying wholly in the invoice month, which Core expresses as no periodisation.
  const rows = PeriodisationRowsHelper.rowsToSend(value, invoiceMonth);
  if (rows.length === 0) return { periodisation: false };
  if (rows.length > 1)
    return { periodisation: true, periodisationRows: rows.map((r) => PeriodisationRowsHelper.toApiRow(r, value.mode)) };

  // An empty list rather than none tells Core this is one range, so a stored split with the same months is replaced.
  const single = PeriodisationRowsHelper.toApiRow(rows[0], value.mode);
  return {
    periodisation: true,
    periodisationRows: [],
    periodisationStartYear: single.periodisationStartYear,
    periodisationStartMonth: single.periodisationStartMonth,
    periodisationEndYear: single.periodisationEndYear,
    periodisationEndMonth: single.periodisationEndMonth,
    periodisationStartDate: single.periodisationStartDate,
    periodisationEndDate: single.periodisationEndDate,
  };
};

const IncomingInvoicePeriodSection: React.FC<IncomingInvoicePeriodSectionProps> = (props) => {
  const { invoice, loadInvoice, fileViewer } = props;
  const incomingInvoiceService = useIncomingInvoiceService();
  const companyNoVat = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const formValue = PeriodisationRowsHelper.fromInvoice(invoice);
  const hasPeriodisation = PeriodisationRowsHelper.hasPeriodisation(formValue);
  const netAmount = PeriodisationRowsHelper.netAmount(invoice.amount, invoice.tax, companyNoVat);

  const save = async (value: IncomingInvoiceFormTypes.PeriodisationRowsFormValue) => {
    await incomingInvoiceService.editPeriodisation(
      invoice.id,
      toPut(value, PeriodisationRowsHelper.invoiceMonth(invoice.invoiceDate))
    );
    loadInvoice && loadInvoice();
  };

  const editButtonLabel = !hasPeriodisation
    ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Add)
    : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit);

  return (
    <GroupSectionInline
      key={`incoming-invoice-section-periods`}
      label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
      icon={IconDefinitionConstants.WMonthCalendar}
    >
      {!invoice.isPeriodisationEditable ? (
        <StaticInfoMessage>
          {LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.IncomingInvoicePeriodisationNotEditable
          )}
        </StaticInfoMessage>
      ) : (
        <DialogEvents
          render={(isOpen, openDialog, closeDialog, key) => (
            <Grid
              container
              className={clsx(
                cssExports.readOnlyForm,
                cssExports.groupSectionInlineDetailsTable,
                cssExports.detailsTableFitContent
              )}
            >
              <Grid container spacing={2} alignItems="center">
                {hasPeriodisation && (
                  <Grid container item>
                    <DetailsTableViewSmall
                      detailsList={AccrualHelper.getPeriodisationRowsKeyValueArray(
                        formValue,
                        netAmount,
                        invoice.currency,
                        invoice.isCredit
                      )}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Button className={clsx(cssExports.button, cssExports.btnSmall)} onClick={openDialog}>
                    {editButtonLabel}
                  </Button>
                </Grid>
              </Grid>
              <PeriodisationAdvancedDialog
                key={key}
                isOpen={isOpen}
                closeDialog={closeDialog}
                value={formValue}
                netAmount={netAmount}
                currency={invoice.currency}
                fileViewer={fileViewer}
                invoiceMonth={PeriodisationRowsHelper.invoiceMonth(invoice.invoiceDate)}
                isCredit={invoice.isCredit}
                onSave={save}
                successMessage={TranslationKeys.Common.Saved}
              />
            </Grid>
          )}
        />
      )}
    </GroupSectionInline>
  );
};

export default IncomingInvoicePeriodSection;
